import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/** Identidad de la apertura (p. ej. id de la entidad editada); ``null`` = cerrado. */
export type ModalIdentity = string | number | null;

interface ModalLifecycleOptions {
    /**
     * Identidad render-current de la apertura. Cada cambio (abrir, cerrar,
     * cambiar de entidad) estrena una clave EN RENDER: se invalida la
     * generación y se apagan los handlers en el mismo render que trae la
     * identidad nueva, antes de cualquier evento, efecto o continuación.
     */
    identity: ModalIdentity;
    /** Aviso al padre; ``close`` lo invoca UNA sola vez por apertura. */
    onClose: () => void;
    /**
     * Repone el estado visual de la identidad nueva (setState) y recibe su
     * clave. Corre en layout con cada clave nueva y la identidad solo se
     * habilita en el commit que ya muestra ese estado repuesto.
     */
    onReset?: (identityKey: number) => void;
}

export interface ModalLifecycle {
    /** Clave de la identidad vigente (cambia en render al cambiar ``identity``). */
    identityKey: number;
    /** Hay identidad (``identity !== null``): el diálogo se pinta. */
    rendered: boolean;
    /** Generación vigente, para capturarla antes de un ``await`` sin invalidar nada. */
    generation: () => number;
    /** Invalida todo lo pendiente y devuelve la generación nueva (cargas al abrir). */
    invalidate: () => number;
    /** Una continuación puede aplicar: montado, identidad activa y misma generación. */
    isLive: (generation: number) => boolean;
    /** Un evento puede escribir: identidad activa (reset en pantalla, sin cierre) y sin escritura en curso. */
    canWrite: () => boolean;
    /**
     * Toma el bloqueo SÍNCRONO de escritura (dos envíos en el mismo turno =
     * uno). Devuelve la generación a verificar tras cada ``await`` o ``null``
     * si no procede (cerrado, identidad en frontera o ya escribiendo).
     */
    beginWrite: () => number | null;
    /** Libera el bloqueo solo si esa generación sigue viva; la siguiente identidad arranca libre. */
    endWrite: (generation: number) => void;
    /**
     * Cierre terminal e idempotente: apaga la identidad e invalida lo pendiente
     * ANTES de avisar al padre, una sola vez por apertura. Si el padre deja el
     * DOM montado, los handlers y las respuestas quedan inertes hasta una
     * identidad nueva.
     */
    close: () => void;
}

/**
 * Ciclo de vida asíncrono de un diálogo modal: identidad de apertura fijada
 * en render, generación de peticiones, bloqueo síncrono de escritura,
 * habilitación tras el reset y cierre único. Complementa a ``useModalFocus``
 * (foco/teclado), que sigue siendo independiente.
 *
 * Contrato para el componente:
 *  - capturar ``generation()`` (o ``beginWrite()``) antes de cada ``await`` y
 *    comprobar ``isLive`` después de CADA uno, antes de tocar estado o llamar
 *    callbacks;
 *  - leer el estado visual derivado por ``identityKey`` (o reponerlo en
 *    ``onReset``) para que la identidad anterior nunca se pinte como la nueva;
 *  - cerrar siempre con ``close``.
 */
export const useModalLifecycle = ({ identity, onClose, onReset }: ModalLifecycleOptions): ModalLifecycle => {
    const identityRef = useRef<{ key: number; identity: ModalIdentity }>({ key: 0, identity: null });
    const generationRef = useRef(0);
    const mountedRef = useRef(true);
    // Espejos síncronos: handlers habilitados, intención de cierre, escritura en curso.
    const activeRef = useRef(false);
    const closedRef = useRef(false);
    const writingRef = useRef(false);
    // Identidad cuyo reset ya está en pantalla.
    const [readyKey, setReadyKey] = useState(0);
    const onResetRef = useRef(onReset);

    // Detección EN RENDER del cambio de identidad (sin setState): idempotente
    // ante renders repetidos o descartados (solo cambia si ``identity`` cambió).
    if (identityRef.current.identity !== identity) {
        identityRef.current = { key: identityRef.current.key + 1, identity };
        generationRef.current += 1;
        activeRef.current = false;
        closedRef.current = false;
        writingRef.current = false;
    }
    const identityKey = identityRef.current.key;
    const rendered = identity !== null;

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; generationRef.current += 1; activeRef.current = false; };
    }, []);

    // Reset visual de la identidad nueva (layout: el estado anterior no llega a
    // pintarse) y marca de "listo" en la misma actualización.
    useLayoutEffect(() => { onResetRef.current = onReset; });
    useLayoutEffect(() => {
        onResetRef.current?.(identityKey);
        setReadyKey(identityKey);
    }, [identityKey]);

    // Habilitación segura tras CADA commit: la identidad vigente queda activa
    // solo si su reset ya está en pantalla y no hay intención de cierre.
    useLayoutEffect(() => {
        activeRef.current = rendered && readyKey === identityKey && !closedRef.current;
    });

    const close = useCallback(() => {
        if (closedRef.current) return;
        closedRef.current = true;
        activeRef.current = false;
        generationRef.current += 1;
        onClose();
    }, [onClose]);

    const api = useMemo(() => {
        const isLive = (generation: number) =>
            mountedRef.current && activeRef.current && generationRef.current === generation;
        const canWrite = () => activeRef.current && !writingRef.current;
        return {
            generation: () => generationRef.current,
            invalidate: () => ++generationRef.current,
            isLive,
            canWrite,
            beginWrite: () => {
                if (!canWrite()) return null;
                writingRef.current = true;
                return generationRef.current;
            },
            endWrite: (generation: number) => { if (isLive(generation)) writingRef.current = false; },
        };
    }, []);

    return { identityKey, rendered, close, ...api };
};
